import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react'
import ConfigurationCenter from './configurationcenter';
import { Link, useNavigate } from 'react-router-dom';
import AdminChartComponent from './admincharts';
import useHandleErrors from '../../utils/user.utils';
import { toast } from 'react-toastify';
import { useUser } from '../../utils/userContext';
import moment from 'moment';

const AdminDashboard = () => {
    const [totalProjectsCount, setTotalProjectsCount] = useState(0);
    const [activeProjectsCount, setActiveProjectsCount] = useState(0);
    const [onHoldProjectsCount, setOnHoldProjectsCount] = useState(0);
    const token = localStorage.getItem("token");
    const handleErrors = useHandleErrors();
    const hasFetchedRef = useRef(false);
    const navigate = useNavigate();
    const { user } = useUser();

    useEffect(() => {
        const fetchData = async () => {
            try {
                await fetchProjectDetails();
                await fetchAssetDetails();
                await fetchClaimsDetails();
                await fetchTimesheetDetails();
                await fetchEmployeeReportDetails();
                await fetchsummary();
                await getPersonalDetails();
                await fetchViewTimesheetDetails();
                await fetchHolidays();
                await fetchAnnouncements();
            } catch (error) {
                if (error.response) {
                    if (error.response && error.response.status === 401) {
                        toast.error("Session expired. Please log in again.");
                        navigate("/")
                    } else {
                        handleErrors(error.response);
                    }
                } else {
                    toast.error("Something went wrong. Please try again later.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
            }
        };
        if (!hasFetchedRef.current) {
            fetchData();
            hasFetchedRef.current = true;
        }
    }, []);

    // Function to fetch project details including counts from API
    const fetchProjectDetails = async () => {
        try {
            const path = "/getProjectDetails";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.status === 200) {
                const data = await response.data;
                console.log("data.GetOnboardedProjects.length", data.GetOnboardedProjects.length)
                // Calculate counts
                setTotalProjectsCount(data.GetOnboardedProjects.length);
                const activeProjects = data.GetOnboardedProjects.filter(project => project.ProjectStatus === 'Active');
                setActiveProjectsCount(activeProjects.length);
                const onHoldProjects = data.GetOnboardedProjects.filter(project => project.ProjectStatus === 'On Hold');
                setOnHoldProjectsCount(onHoldProjects.length);
            } else {
                handleErrors(response)
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 404) {
                    // Handle 404 status code separately
                    setTotalProjectsCount(0);
                    setActiveProjectsCount(0);
                    setOnHoldProjectsCount(0);
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    };

    const [totalAssetsCount, setTotalAssetsCount] = useState(0);
    const [activeAssetsCount, setActiveAssetsCount] = useState(0);
    const [inactiveAssetsCount, setInactiveAssetsCount] = useState(0);
    const [badAssetsCount, setBadAssetsCount] = useState(0);
    const currentYear = new Date().getFullYear();

    // Function to fetch asset details including counts from API
    const fetchAssetDetails = async () => {
        try {
            const path = "/getAssetReport";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.status === 200) {
                const data = await response.data;
                // Calculate counts
                setTotalAssetsCount(data?.RegisterAssets?.length);
                const activeAssets = data?.RegisterAssets?.filter(asset => asset.Status.toLowerCase() === 'active');
                setActiveAssetsCount(activeAssets?.length);
                const inactiveAssets = data?.RegisterAssets?.filter(asset => asset.Status.toLowerCase() === 'inactive');
                setInactiveAssetsCount(inactiveAssets?.length);
                const badAssets = data?.RegisterAssets?.filter(asset => asset.AssetCondition.toLowerCase() === 'bad');
                setBadAssetsCount(badAssets?.length);
            } else {
                console.error('Failed to fetch asset details');
                handleErrors(response)
            }
        } catch (error) {
            console.error('Error fetching asset details:', error);
            if (error.response) {
                if (error.response.status === 404) {
                    // Handle 404 status code separately
                    setTotalAssetsCount(0);
                    setActiveAssetsCount(0);
                    setInactiveAssetsCount(0);
                    setBadAssetsCount(0);
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    };


    const [totalClaimsCount, setTotalClaimsCount] = useState(0);
    const [pendingClaimsCount, setPendingClaimsCount] = useState(0);
    const [rejectedClaimsCount, setRejectedClaimsCount] = useState(0);

    // Function to fetch claims details including counts from API
    const fetchClaimsDetails = async () => {
        try {
            const path = "/getSubmitClaimsDetails";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }); // Replace with your API endpoint
            if (response.status === 200) {
                const data = await response.data;
                // Calculate counts
                setTotalClaimsCount(data.listSubmitClaims.length);
                const pendingClaims = data.listSubmitClaims.filter(claim => claim?.Status?.toLowerCase() === 'pending');
                setPendingClaimsCount(pendingClaims.length);
                const rejectedClaims = data.listSubmitClaims.filter(claim => claim?.Status?.toLowerCase() === 'approved');
                setRejectedClaimsCount(rejectedClaims.length);
            } else {
                console.error('Failed to fetch claims details');
                handleErrors(response)
            }
        } catch (error) {
            console.error('Error fetching claims details:', error);
            if (error.response) {
                if (error.response.status === 404) {
                    // Handle 404 status code separately
                    setTotalClaimsCount(0);
                    setPendingClaimsCount(0);
                    setRejectedClaimsCount(0);
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    };

    const [pendingRequestsCount, setPendingRequestsCount] = useState(0);

    // Function to fetch timesheet details including pending request count from API
    const fetchTimesheetDetails = async () => {
        try {
            const path = `/submitTimesheetDetails?emailId=${user?.OfficialEmail}`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }); // Replace with your API endpoint
            if (response.status === 200) {
                const data = await response.data;
                // Calculate pending requests count
                let pendingRequests = 0;
                data.listTimesheetDetails.forEach(timesheet => {
                    pendingRequests += timesheet.timesheetSummary.filter(summary => summary.status.toLowerCase() === 'submitted').length;
                });
                setPendingRequestsCount(pendingRequests);
            } else {
                console.error('Failed to fetch timesheet details');
                handleErrors(response)
            }
        } catch (error) {
            console.error('Error fetching timesheet details:', error);
            if (error.response) {
                if (error.response.status === 404) {
                    // Handle 404 status code separately
                    setPendingRequestsCount(0);
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    };

    const [showModal, setShowModal] = useState(false);

    const openModal = () => {
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    const [data, setData] = useState({
        onboarded: [],
        offboarded: [],
    });

    const fetchEmployeeReportDetails = async () => {
        try {
            const path = "/getEmployeeMasterReport";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            if (response.status === 200) {
                setData({
                    onboarded: response.data.onboardEmployeeDetails,
                    offboarded: response.data.offboardEmployeeDetails,
                });
            } else {
                handleErrors(response)
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 404) {
                    // Handle 404 status code separately
                    setData({
                        onboarded: [],
                        offboarded: [],
                    });
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    }

    const totalEmployees = data.onboarded.length + data.offboarded.length;
    const onboardedCount = data.onboarded.length;
    const offboardedCount = data.offboarded.length;


    const getCurrentMonthOnboardedCount = () => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        return data.onboarded.filter(employee => {
            const createdDate = new Date(employee.createdDate);
            return createdDate.getMonth() === currentMonth && createdDate.getFullYear() === currentYear;
        }).length;
    };

    const currentMonthOnboardedCount = getCurrentMonthOnboardedCount();
    const currentMonthOnboardedPercentage = onboardedCount > 0 ? (currentMonthOnboardedCount / onboardedCount) * 100 : 0;


    const [vacations, setVacations] = useState([]);

    const fetchsummary = async () => {
        try {
            const path = "/LeaveTracker/summary";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            if (response.status === 200) {
                setVacations(response.data);
            }
            else {
                handleErrors(response)
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 404) {
                    // Handle 404 status code separately
                    setVacations([]);
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    }

    const getCurrentMonthVacationCount = () => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        return vacations.filter(vacation => {
            const startDate = new Date(vacation.startDate);
            const endDate = new Date(vacation.endDate);
            return (startDate.getMonth() === currentMonth && startDate.getFullYear() === currentYear) ||
                (endDate.getMonth() === currentMonth && endDate.getFullYear() === currentYear);
        }).length;
    };

    const currentMonthVacationCount = getCurrentMonthVacationCount();

    const [timesheets, setTimesheets] = useState([]);

    const fetchViewTimesheetDetails = async () => {
        try {
            const path = "/viewTimesheet";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            if (response.status === 200) {
                setTimesheets(response.data.listTimesheetDetails);
            } else {
                handleErrors(response)
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 404) {
                    // Handle 404 status code separately
                    setTimesheets([]);
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    }

    const getCurrentMonthAttendancePercentage = () => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        let attendedEmployees = new Set();

        timesheets.forEach(timesheet => {
            timesheet.timesheetSummary.forEach(summary => {
                summary.timeSheetHours.forEach(hourEntry => {
                    const date = new Date(hourEntry.weekDate);
                    if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
                        if (hourEntry.projectHours.some(project => project.hours > 0)) {
                            attendedEmployees.add(timesheet.employeeId);
                        }
                    }
                });
            });
        });

        const attendedCount = attendedEmployees.size;
        return onboardedCount > 0 ? (attendedCount / onboardedCount) * 100 : 0;
    };

    const currentMonthAttendancePercentage = getCurrentMonthAttendancePercentage();


    // mydocuments
    const [employeePercentage, setEmployeePercentage] = useState(null);
    const [personalInfo, setpersonalInfo] = useState(null);
    const [documents, setDocuments] = useState([]);

    const getFileName = (url) => {
        return url.substring(url.lastIndexOf('/') + 1);
    };

    useEffect(() => {
        console.log("personalInfo", personalInfo?.personalDetails?.experience?.totalExperience)
        if (personalInfo && personalInfo.personalDetails) {
            const docs = [];
            if (personalInfo?.personalDetails?.family?.birthCertificate) {
                docs.push({
                    name: 'Birth Certificate',
                    url: getFileName(personalInfo?.personalDetails?.family?.birthCertificate),
                    uploadedOn: '01-01-2025' // Replace with actual date logic if available
                });
            }
            if (personalInfo?.personalDetails?.family?.marriageCertificate) {
                docs.push({
                    name: 'Marriage Certificate',
                    url: getFileName(personalInfo?.personalDetails?.family?.marriageCertificate),
                    uploadedOn: '01-01-2025' // Replace with actual date logic if available
                });
            }
            if (personalInfo?.personalDetails?.health?.healthCertificate) {
                docs.push({
                    name: 'Health Certificate',
                    url: getFileName(personalInfo?.personalDetails?.health?.healthCertificate),
                    uploadedOn: '01-01-2025' // Replace with actual date logic if available
                });
            }
            if (personalInfo?.personalDetails?.additionalInformation?.passportCopy) {
                docs.push({
                    name: 'Passport Copy',
                    url: getFileName(personalInfo?.personalDetails?.additionalInformation?.passportCopy),
                    uploadedOn: '01-01-2025' // Replace with actual date logic if available
                });
            }
            if (personalInfo?.personalDetails?.additionalInformation?.conductCertificates) {
                docs.push({
                    name: 'Conduct Certificate',
                    url: getFileName(personalInfo?.personalDetails?.additionalInformation?.conductCertificates),
                    uploadedOn: '01-01-2025' // Replace with actual date logic if available
                });
            }
            // Add other documents as needed

            setDocuments(docs);
        }
    }, [personalInfo]);

    const getPersonalDetails = async () => {
        try {
            if (user) {
                const path = `/getPersonalDetails?EmployeeId=${user?.EmployeeId}`;
                const URL = process.env.REACT_APP_BE_URL + path;
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                if (response.status === 200) {
                    setEmployeePercentage(response.data)
                    setpersonalInfo(response.data)
                } else {
                    handleErrors(response);
                }
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        }
    }

    // holidays
    // Fetch Uploaded Files
    const [holidays, setHolidays] = useState([]);

    const fetchHolidays = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BE_URL}/getHolidays`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 200) {
                setHolidays(response.data || []);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            setHolidays([]); // Show "No Data" if error occurs
        }
    };

    // announcements
    const [announcements, setAnnouncements] = useState([]);

    const fetchAnnouncements = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BE_URL}/getAnnouncements`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 200) {
                console.log("announcements", response.data || [])
                setAnnouncements(response.data || []);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            setAnnouncements([]); // Show "No Data" if error occurs
        }
    };

    // empsat
    const [goodPercentage, setGoodPercentage] = useState(0);
    const [badPercentage, setBadPercentage] = useState(0);
    const [empSatisfactions, setEmpSatisfactions] = useState([]);
    const [showEsatModal, setShowEsatModal] = useState(false);

    useEffect(() => {
        const fetchSatisfactionData = async () => {
            try {
                const path = `/employeeSatisfactionDetails`;
                const URL = process.env.REACT_APP_BE_URL + path;
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const allSats = response.data.empSatisfactions || [];
                const total = allSats.length;
                const goodCount = allSats.filter(item => item.message === "Good").length;
                const badCount = allSats.filter(item => item.message === "Bad").length;

                setEmpSatisfactions(allSats); // Show all data

                if (total > 0) {
                    setGoodPercentage(Math.round((goodCount / total) * 100));
                    setBadPercentage(Math.round((badCount / total) * 100));
                }
            } catch (error) {
                console.error("Error fetching satisfaction data", error);
            }
        };

        fetchSatisfactionData();
    }, []);



    return (
        <div>
            <div class="content-page">
                <div className="app-cc-trigger">
                    <button type="button" className="btn" title="Configuration Center" onClick={openModal}>
                        <i className="ri-equalizer-fill"></i>
                    </button>
                </div>
                <ConfigurationCenter showModal={showModal} closeModal={closeModal} />
                <div class="container-fluid">
                    <div class="row">
                        <div class="col-lg-12">
                            <div class="row mb-3">
                                <div class="col">
                                    <h3 class="title mb-2">Welcome to <span>Work</span><span>Wise</span> 360!</h3>
                                </div>
                                <div className="col-auto pr-0">
                                    <div className="switch-btn">
                                        <input
                                            className="switch"
                                            type="checkbox"
                                            id="switch-view"
                                            name="switch-view-checkbox"
                                            value="switch-view-checkbox"
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    window.location.href = '/home';  // Redirect to admin dashboard
                                                } else {
                                                    window.location.href = '/Dashboard';  // Redirect to user dashboard
                                                }
                                            }}
                                        />
                                        <label htmlFor="switch-view" className="switch-label">
                                            <i className="ri-user-line"></i>
                                            <div className="action">
                                                <span className="option-1">Switch User View</span>
                                                <span className="option-2">Switch Admin View</span>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                                <div class="col-auto"><Link to={'/Employees'} class="btn btn-primary mt-lg-0 mt-4"><i class="ri-user-star-line"></i> View All Employees</Link></div>
                            </div>
                        </div>

                        <div class="col-lg-12">
                            <div class="row">
                                <div className="col-lg-3 col-md-6">
                                    <div className="card dashboard-card mb-4 green">
                                        <div className="card-body pt-2 position-relative">
                                            <Link to={'/Employees'} target="_blank" className="stretched-link"></Link>
                                            <div className="d-flex align-items-top justify-content-between">
                                                <div className="icon icon-box rounded">
                                                    <i className="ri-group-fill"></i>
                                                </div>
                                                <h6>{totalEmployees}<span> Users</span></h6>
                                            </div>
                                            <h5 className="title mb-0">Employees</h5>
                                            <small className="mb-0 text-muted">View Details</small>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="dashboard-card-table">
                                                <table className="table mb-0">
                                                    <tbody>
                                                        <tr>
                                                            <td><span className="legend-indicator bg-success"></span>Onboarded Employees</td>
                                                            <td className="text-right">{onboardedCount}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><span className="legend-indicator bg-warning"></span>Offboarded Employees</td>
                                                            <td className="text-right">{offboardedCount}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-3 col-md-6">
                                    <div className="card dashboard-card mb-4 blue">
                                        <div className="card-body pt-2 position-relative">
                                            <a href="/OnboardProjects" className="stretched-link"></a>
                                            <div className="d-flex align-items-top justify-content-between">
                                                <div className="icon icon-box rounded">
                                                    <i className="ri-rocket-fill"></i>
                                                </div>
                                                <h6>{totalProjectsCount}<span>As on May 2025</span></h6>
                                            </div>
                                            <h5 className="title mb-0">Projects</h5>
                                            <small className="mb-0 text-muted">View Details</small>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="dashboard-card-table">
                                                <table className="table mb-0">
                                                    <tbody>
                                                        <tr>
                                                            <td><span className="legend-indicator bg-success"></span>Active Projects</td>
                                                            <td className="text-right">{activeProjectsCount}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><span className="legend-indicator bg-warning"></span>On Hold</td>
                                                            <td className="text-right">{onHoldProjectsCount}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-3 col-md-6">
                                    <div className="card dashboard-card mb-4 green">
                                        <div className="card-body pt-2 position-relative">
                                            <a href="/AssetInventory" target="_blank" className="stretched-link"></a>
                                            <div className="d-flex align-items-top justify-content-between">
                                                <div className="icon icon-box rounded">
                                                    <i className="ri-shapes-fill"></i>
                                                </div>
                                                <h6>{totalAssetsCount}<span>Active</span></h6>
                                            </div>
                                            <h5 className="title mb-0">Total Assets</h5>
                                            <small className="mb-0 text-muted">View Details</small>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="dashboard-card-table">
                                                <table className="table mb-0">
                                                    <tbody>
                                                        <tr>
                                                            <td><span className="legend-indicator bg-info"></span>Active</td>
                                                            <td className="text-right">{activeAssetsCount}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><span className="legend-indicator bg-light"></span>Inactive</td>
                                                            <td className="text-right">{inactiveAssetsCount}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-3 col-md-6">
                                    <div className="card dashboard-card mb-4 blue">
                                        <div className="card-body pt-2 position-relative">
                                            <a href="/ClaimReport" className="stretched-link"></a>
                                            <div className="d-flex align-items-top justify-content-between">
                                                <div className="icon icon-box rounded">
                                                    <i className="ri-receipt-fill"></i>
                                                </div>
                                                <h6>{totalClaimsCount}<span>Year 2025</span></h6>
                                            </div>
                                            <h5 className="title mb-0">Claims</h5>
                                            <small className="mb-0 text-muted">View Details</small>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="dashboard-card-table">
                                                <table className="table mb-0">
                                                    <tbody>
                                                        <tr>
                                                            <td><span className="legend-indicator bg-success"></span>Approved</td>
                                                            <td className="text-right">{rejectedClaimsCount}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><span className="legend-indicator bg-warning"></span>Pending</td>
                                                            <td className="text-right">{pendingClaimsCount}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-6">
                            <div class="row">
                                <div className="col-lg-6 col-md-6">
                                    <div className="card dashboard-card mb-4 amber">
                                        <div className="card-body pt-2">
                                            <a href={"/ApproveTimesheet"} target="_blank" className="stretched-link"></a>
                                            <div className="d-flex align-items-top justify-content-between">
                                                <div className="icon icon-box rounded">
                                                    <i className="ri-calendar-schedule-fill"></i>
                                                </div>
                                                <h6>
                                                    <small className="status-tag">Pending</small>
                                                    {pendingRequestsCount}<span>Request(s)</span>
                                                </h6>
                                            </div>
                                            <h5 className="title mb-0">Approve Timesheet</h5>
                                            <small className="mb-0 text-muted">View Details</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6">
                                    <div className="card dashboard-card mb-4 red">
                                        <div className="card-body pt-2">
                                            <a href={"/LeaveReport"} target="_blank" className="stretched-link"></a>
                                            <div className="d-flex align-items-top justify-content-between">
                                                <div className="icon icon-box rounded">
                                                    <i className="ri-landscape-fill"></i>
                                                </div>
                                                <h6>
                                                    {currentMonthVacationCount}<span> Current Month</span>
                                                </h6>
                                            </div>
                                            <h5 className="title mb-0">Employees On Vacation</h5>
                                            <small className="mb-0 text-muted">View Details</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div>
                                    <div id='currentAttritionTrend' style={{ height: '555px' }}><AdminChartComponent /></div>
                                </div>
                            </div>
                        </div>
                        <div class="col-lg-6">
                            <div class="row">
                                <div className="col-lg-4 col-md-4 col-sm-12">
                                    <div className="card border-none mb-4">
                                        <div className="card-body text-center p-0">
                                            <div onClick={() => setShowEsatModal(true)} className="stretched-link" style={{ cursor: "pointer" }}></div>
                                            <div className="current-month-attendance">
                                                <svg width="100%" height="100%" viewBox="0 0 50 50" className="donut">
                                                    {/* Background Circle */}
                                                    <circle
                                                        className="donut-ring"
                                                        cx="23"
                                                        cy="23"
                                                        r="15.91549430918954"
                                                        fill="transparent"
                                                        stroke="#f3f3f3"
                                                        strokeWidth="2"
                                                    ></circle>

                                                    {/* Good Segment */}
                                                    <circle
                                                        className="donut-segment"
                                                        cx="23"
                                                        cy="23"
                                                        r="15.91549430918954"
                                                        fill="transparent"
                                                        stroke="#28a745"
                                                        strokeWidth="2"
                                                        strokeDasharray={`${goodPercentage} ${100 - goodPercentage}`}
                                                        strokeDashoffset="25"
                                                    >
                                                        <title>Good: {goodPercentage}%</title>
                                                    </circle>

                                                    {/* Bad Segment (starts where Good ends) */}
                                                    <circle
                                                        className="donut-segment"
                                                        cx="23"
                                                        cy="23"
                                                        r="15.91549430918954"
                                                        fill="transparent"
                                                        stroke="#dc3545"
                                                        strokeWidth="2"
                                                        strokeDasharray={`${badPercentage} ${100 - badPercentage}`}
                                                        strokeDashoffset={25 - (goodPercentage / 100) * 100}
                                                    >
                                                        <title>Bad: {badPercentage}%</title>
                                                    </circle>

                                                    {/* Center Text */}
                                                    <g className="chart-text">
                                                        <text x="48%" y="40%" className="chart-number" textAnchor="middle" dy="-.2em">
                                                            {goodPercentage}%
                                                        </text>
                                                        <text x="48%" y="35%" className="chart-label" textAnchor="middle" dy=".3em">
                                                            E-SAT Feedback
                                                        </text>
                                                        <text x="48%" y="45%" className="chart-label" textAnchor="middle" dy=".3em">
                                                            <tspan fill="#28a745">●</tspan> <tspan fill="#000">Good ({goodPercentage}%)</tspan>
                                                        </text>
                                                        <text x="45%" y="55%" className="chart-label" textAnchor="middle" dy=".3em">
                                                            <tspan fill="#dc3545">●</tspan> <tspan fill="#000">Bad ({badPercentage}%)</tspan>
                                                        </text>
                                                    </g>

                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-4 col-md-4 col-sm-12">
                                    <div className="card border-none mb-4">
                                        <div className="card-body text-center p-0">
                                            <a href={"/Employees"} className="stretched-link"></a>
                                            <div className="current-month-onboard">
                                                <svg width="100%" height="100%" viewBox="0 0 45 45" className="donut">
                                                    <circle className="donut-segment" cx="23" cy="23" r="15.91549430918954" fill="transparent" stroke="#f3f3f3" strokeWidth="1" strokeDashoffset="25"></circle>
                                                    <circle className="donut-segment" cx="23" cy="23" r="15.91549430918954" fill="transparent" stroke="#25B740" strokeWidth="2" strokeDasharray={`${currentMonthOnboardedPercentage} ${100 - currentMonthOnboardedPercentage}`} strokeDashoffset="25"></circle>
                                                    <g className="chart-text">
                                                        <text x="50%" y="50%" className="chart-number">
                                                            {Math.round(currentMonthOnboardedPercentage)}%
                                                        </text>
                                                        <text x="50%" y="50%" className="chart-label" dy=".3em">
                                                            Current Month
                                                        </text>
                                                        <text x="50%" y="58%" className="chart-label" dy=".3em">
                                                            Onboard
                                                        </text>
                                                    </g>
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-lg-4 col-md-4 col-sm-12">
                                    <div class="card border-none mb-4">
                                        <div class="card-body text-center p-0">
                                            <a href="/AssetMasterReport" class="stretched-link"></a>
                                            <div class="assets-required-action">
                                                <svg width="100%" height="100%" viewBox="0 0 45 45" class="donut">
                                                    <circle class="donut-segment" cx="23" cy="23" r="15.91549430918954" fill="transparent" stroke="#f3f3f3" stroke-width="1" stroke-dashoffset="25"></circle>
                                                    <circle class="donut-segment" cx="23" cy="23" r="15.91549430918954" fill="transparent" stroke="#dc3545" stroke-width="2" stroke-dasharray="10 90" stroke-dashoffset="25"></circle>
                                                    <g class="chart-text">
                                                        <text x="50%" y="50%" class="chart-number">
                                                            {badAssetsCount}%
                                                        </text>
                                                        <text x="50%" y="50%" class="chart-label">
                                                            Assets Required
                                                        </text>
                                                        <text x="50%" y="58%" class="chart-label">
                                                            Attention
                                                        </text>
                                                    </g>
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <ul class="nav nav-pills tab-pills" id="pills-tab" role="tablist">
                                <li class="nav-item">
                                    <a class="nav-link active" data-toggle="pill" href="#announcements" role="tab">Announcements <span class="badge badge-tab">{announcements?.length || 0}</span></a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" data-toggle="pill" href="#holidays" role="tab">Holidays <span class="badge badge-tab">{holidays?.length || 0}</span></a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" data-toggle="pill" href="#my-documents" role="tab">Recent Documents <span class="badge badge-tab">{documents?.length || 0}</span></a>
                                </li>
                            </ul>
                            <div className="tab-content task-content">
                                <div className="tab-pane fade show active" id="announcements" role="tabpanel">
                                    <div className="card border-none">
                                        <div className="card-body">
                                            <div className="announcement-list dashboard-tab-scroll">
                                                <div className="announcement-container">
                                                    <ul className="announcement">
                                                        {announcements && announcements.length > 0 ? (
                                                            announcements.map((announcement, index) => (
                                                                <li className="announcement-item" key={index}>
                                                                    <div className="timestamp">
                                                                        {`${moment(announcement.eventStartDate).format("DD MMMM YYYY")} - Posted By - ${announcement.employeeName}`}
                                                                    </div>
                                                                    <div className="item-title">{announcement.subject}</div>
                                                                    <p>{announcement.details}</p>
                                                                </li>
                                                            ))
                                                        ) : (
                                                            <li className="announcement-item">
                                                                <p>No data.....</p>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="tab-pane fade" id="holidays" role="tabpanel">
                                    <div class="card border-none">
                                        <div class="card-body">
                                            <div class="card-body p-0">
                                                <div class="holiday-list dashboard-tab-scroll">
                                                    <table class="table grid">
                                                        <thead class="bg-light">
                                                            <tr>
                                                                <th>S.No.</th>
                                                                <th>Holidays</th>
                                                                <th>Day</th>
                                                                <th>Date</th>
                                                                <th>Type</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {holidays?.map((holiday, index) => (
                                                                <tr key={index}>
                                                                    <td>{index + 1}</td>
                                                                    <td>{holiday.holidayName}</td>
                                                                    <td>{holiday.day}</td>
                                                                    <td>{moment(holiday.date, "YYYY-MM-DD HH:mm:ss").format("DD-MM-YYYY")}</td>
                                                                    <td>{holiday.type}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Modal */}
            {showEsatModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header py-2">
                                <h6 className="modal-title">Employee Satisfaction</h6>
                                <button className="close" type="button" onClick={() => setShowEsatModal(false)} aria-label="Close">
                                    <span aria-hidden="true">
                                        <i className="ri-close-circle-fill font-size-20"></i>
                                    </span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="table-responsive">
                                    <table className="table grid">
                                        <thead className="bg-light">
                                            <tr>
                                                <th>S.No.</th>
                                                <th>Employee ID</th>
                                                <th>Message</th>
                                                {/* <th>Comment</th> */}
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {empSatisfactions.length > 0 ? (
                                                empSatisfactions.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td>{item.employeeId}</td>
                                                        <td>
                                                            <span
                                                                className={`badge border ${item.message === "Good"
                                                                    ? "border-success text-success"
                                                                    : "border-danger text-danger"
                                                                    } status`}
                                                            >
                                                                {item.message === "Good" ? (
                                                                    <i className="ri-thumb-up-fill"></i>
                                                                ) : (
                                                                    <i className="ri-thumb-down-fill"></i>
                                                                )}{" "}
                                                                {item.message}
                                                            </span>
                                                        </td>
                                                        {/* <td>{item.comments || "—"}</td> */}
                                                        <td>{moment(item.satCreatedDate).format("DD-MM-YYYY")}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center">
                                                        No data available for this month
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminDashboard
